import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { API, AuthContext } from '@/App';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import EventsCalendar from '@/components/EventsCalendar';
import Footer from '@/components/Footer';
import { toast } from 'sonner';
import { Plus, Calendar as CalendarIcon, MapPin, Users, Award, List, CalendarDays, Mail, Phone } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const Events = () => {
  const { user, token } = React.useContext(AuthContext);
  const [events, setEvents] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [attendeesDialogOpen, setAttendeesDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    location: '',
    image: '',
    event_type: 'free',
    price: 0
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/events`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event created successfully!');
      setIsDialogOpen(false);
      setFormData({ title: '', description: '', date: '', location: '', image: '' });
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    }
  };

  const handleRSVP = async (eventId) => {
    try {
      const response = await axios.post(`${API}/events/${eventId}/rsvp`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`RSVP ${response.data.action}!`);
      fetchEvents();
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error('This is a paid event. Please use the payment button.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to update RSVP');
      }
    }
  };

  const handleViewAttendees = async (event) => {
    try {
      setSelectedEvent(event);
      const response = await axios.get(`${API}/events/${event.id}/attendees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttendees(response.data);
      setAttendeesDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load attendees');
    }
  };

  const handlePayment = async (eventId) => {
    try {
      const response = await axios.post(`${API}/events/${eventId}/checkout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payment');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Auto-close datetime picker after selection
    if (e.target.type === 'datetime-local' && value) {
      e.target.blur();
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #FFE5EC 0%, #FFF8F3 100%)' }}>
      <Navigation />
      <div className="container mx-auto px-4 py-8" data-testid="events-page">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
            Community Events
          </h1>
          {user && user.membership_tier !== 'basic' ? (
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-rose-500 hover:bg-rose-600 flex items-center gap-2" data-testid="create-event-button">
                  <Plus className="w-4 h-4" />
                  Create Event
                </Button>
              </DialogTrigger>
            <DialogContent className="sm:max-w-md" data-testid="create-event-dialog">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>Create New Event</DialogTitle>
                <DialogDescription>Organize an event for the community</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="event-form">
                <Input 
                  name="title" 
                  placeholder="Event Title" 
                  value={formData.title} 
                  onChange={handleChange} 
                  required
                  data-testid="event-title-input"
                />
                <Textarea 
                  name="description" 
                  placeholder="Description" 
                  value={formData.description} 
                  onChange={handleChange} 
                  required
                  rows={3}
                  data-testid="event-description-input"
                />
                <Input 
                  name="date" 
                  type="datetime-local" 
                  value={formData.date} 
                  onChange={handleChange} 
                  required
                  data-testid="event-date-input"
                />
                <Input 
                  name="location" 
                  placeholder="Location" 
                  value={formData.location} 
                  onChange={handleChange} 
                  required
                  data-testid="event-location-input"
                />
                <Input 
                  name="image" 
                  placeholder="Image URL (Optional)" 
                  value={formData.image} 
                  onChange={handleChange}
                  data-testid="event-image-input"
                />
                
                <div>
                  <label className="block text-sm font-medium mb-2">Event Type</label>
                  <select
                    name="event_type"
                    value={formData.event_type}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500"
                  >
                    <option value="free">Free Event</option>
                    <option value="paid">Paid Event</option>
                  </select>
                </div>
                
                {formData.event_type === 'paid' && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Price (€)</label>
                    <Input
                      name="price"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Enter price"
                      value={formData.price}
                      onChange={handleChange}
                      required
                    />
                  </div>
                )}
                
                <Button type="submit" className="w-full bg-rose-500 hover:bg-rose-600" data-testid="event-submit-button">Create Event</Button>
              </form>
            </DialogContent>
          </Dialog>
          ) : (
            <Button 
              onClick={() => navigate('/membership')} 
              className="bg-rose-500 hover:bg-rose-600 flex items-center gap-2"
              data-testid="upgrade-to-create-event"
            >
              <Plus className="w-4 h-4" />
              Upgrade to Create Events
            </Button>
          )}
        </div>

        {/* View Toggle */}
        <Tabs defaultValue="list" className="w-full mb-6" onValueChange={(value) => setViewMode(value)}>
          <TabsList className="glass grid w-full max-w-md grid-cols-2 mx-auto mb-8">
            <TabsTrigger value="list" className="flex items-center gap-2">
              <List className="w-4 h-4" />
              List View
            </TabsTrigger>
            <TabsTrigger value="calendar" className="flex items-center gap-2">
              <CalendarDays className="w-4 h-4" />
              Calendar View
            </TabsTrigger>
          </TabsList>

          <TabsContent value="list">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.map((event) => {
                const isAttending = event.attendees.includes(user?.id);
                const isOrganizer = event.organizer_id === user?.id;
                return (
                  <Card key={event.id} className="glass hover:shadow-xl transition-all relative" data-testid={`event-card-${event.id}`}>
                    {event.is_sponsored && (
                      <Badge className="absolute top-4 right-4 bg-gradient-to-r from-yellow-500 to-amber-600 text-white z-10">
                        <Award className="w-3 h-3 mr-1" />
                        Sponsored
                      </Badge>
                    )}
                    {event.image && (
                      <div className="w-full h-40 overflow-hidden rounded-t-lg">
                        <img src={event.image} alt={event.title} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <CardHeader>
                      <div className="flex items-start justify-between gap-2">
                        <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>{event.title}</CardTitle>
                        {event.event_type === 'paid' && (
                          <Badge className="bg-green-100 text-green-800 border-green-300">
                            €{event.price}
                          </Badge>
                        )}
                        {event.event_type === 'free' && (
                          <Badge className="bg-blue-100 text-blue-800 border-blue-300">
                            FREE
                          </Badge>
                        )}
                      </div>
                      <CardDescription>{event.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <CalendarIcon className="w-4 h-4" />
                        <span>{new Date(event.date).toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <MapPin className="w-4 h-4" />
                        <span>{event.location}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Users className="w-4 h-4" />
                        <span>{event.attendees.length} attending</span>
                      </div>
                      <div className="text-xs text-gray-500">Organized by {event.organizer_name}</div>
                      {event.is_sponsored && event.sponsor_name && (
                        <div className="mt-2 p-2 bg-amber-50 rounded-md border border-amber-200">
                          <p className="text-xs text-amber-900">
                            <Award className="w-3 h-3 inline mr-1" />
                            Sponsored by <span className="font-semibold">{event.sponsor_name}</span>
                          </p>
                        </div>
                      )}
                      
                      {/* Organizer View - Show View Attendees button */}
                      {isOrganizer ? (
                        <Button 
                          onClick={() => handleViewAttendees(event)} 
                          variant="outline"
                          className="w-full text-rose-600 border-rose-300"
                        >
                          View Attendees ({event.attendees.length})
                        </Button>
                      ) : (
                        // Regular attendee view
                        event.event_type === 'paid' ? (
                          // Paid event - show payment button or attending status
                          isAttending ? (
                            <div className="space-y-2">
                              <Badge className="w-full justify-center py-2 bg-green-100 text-green-800">
                                ✓ Registered & Paid
                              </Badge>
                            </div>
                          ) : (
                            <Button 
                              onClick={() => handlePayment(event.id)} 
                              className="w-full bg-green-600 hover:bg-green-700"
                            >
                              Register & Pay €{event.price}
                            </Button>
                          )
                        ) : (
                          // Free event - show RSVP button (or Sign In prompt for anonymous visitors)
                          user ? (
                            <Button 
                              onClick={() => handleRSVP(event.id)} 
                              className={`w-full ${isAttending ? 'bg-gray-500 hover:bg-gray-600' : 'bg-rose-500 hover:bg-rose-600'}`}
                              data-testid={`rsvp-${event.id}`}
                            >
                              {isAttending ? 'Cancel RSVP' : 'RSVP'}
                            </Button>
                          ) : (
                            <Button
                              onClick={() => navigate('/')}
                              className="w-full bg-rose-500 hover:bg-rose-600"
                              data-testid={`signin-to-rsvp-${event.id}`}
                            >
                              Sign in to RSVP
                            </Button>
                          )
                        )
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="calendar">
            <EventsCalendar 
              events={events}
              onSelectEvent={(event) => {
                // Show event details when clicking on calendar event
                toast.info(`Event: ${event.title}`);
              }}
              onRSVP={handleRSVP}
            />
          </TabsContent>
        </Tabs>

        {events.length === 0 && (
          <div className="text-center py-12" data-testid="no-events">
            <p className="text-gray-500">No events scheduled yet</p>
          </div>
        )}

        {/* Attendees Dialog */}
        <Dialog open={attendeesDialogOpen} onOpenChange={setAttendeesDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Attendees for: {selectedEvent?.title}
              </DialogTitle>
              <DialogDescription>
                {attendees.length} {attendees.length === 1 ? 'person is' : 'people are'} attending this event
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {attendees.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No attendees yet</p>
              ) : (
                attendees.map((attendee) => (
                  <Card key={attendee.id} className="bg-white">
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4">
                        {attendee.avatar_url && (
                          <img 
                            src={attendee.avatar_url} 
                            alt={attendee.full_name} 
                            className="w-16 h-16 rounded-full object-cover"
                          />
                        )}
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg text-gray-900">{attendee.full_name}</h3>
                          {attendee.business_name && (
                            <p className="text-sm text-gray-600">{attendee.business_name}</p>
                          )}
                          {attendee.industry && (
                            <Badge variant="outline" className="mt-2">{attendee.industry}</Badge>
                          )}
                          
                          <div className="mt-3 space-y-1">
                            <div className="flex items-center gap-2 text-sm text-gray-700">
                              <Mail className="w-4 h-4 text-gray-500" />
                              <a href={`mailto:${attendee.email}`} className="hover:text-rose-600">
                                {attendee.email}
                              </a>
                            </div>
                            {attendee.phone && (
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <Phone className="w-4 h-4 text-gray-500" />
                                <a href={`tel:${attendee.phone}`} className="hover:text-rose-600">
                                  {attendee.phone}
                                </a>
                              </div>
                            )}
                          </div>
                          
                          <div className="mt-4 flex gap-2">
                            <Button
                              onClick={() => window.location.href = `/profile/${attendee.id}`}
                              size="sm"
                              variant="outline"
                              className="text-rose-600 border-rose-300"
                            >
                              View Profile
                            </Button>
                            <Button
                              onClick={() => window.location.href = `mailto:${attendee.email}`}
                              size="sm"
                              className="bg-rose-600 hover:bg-rose-700"
                            >
                              Contact
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <Footer />
    </div>
  );
};

export default Events;